import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';

import BixToggleCellRenderer from '$/components/global/bixToggleCellRenderer';
import AnalyticsHeaderFilter from '$/components/analyticsHeaderFilter';
import Loading from '$/components/loading/Loading';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import {
  getIsCurrentUserClientState,
  getIsCurrentUserCreditSightsAnalystState,
  getIsCurrentUserCreditSightsOpsState,
  getIsCurrentUserRatingsAnalystState,
  getIsCurrentUserSuFAnalystState,
} from '$/store/profile/selectors';
import { getIsCurrentUserAdminState } from '$/store/profile/selectors';
import SettingsNavigation from '../SettingsNavigation';
import { AccountService } from '$/services/AccountService';
import { toastError } from '$/store/toast/actions';
import './index.scss';

interface IMyIndustryProps {
  isAdmin: boolean;
  isClient: boolean;
  isCreditSightsOpsSufOrRatings: boolean;
  dispatchToastError: (message: string) => Promise<void>;
}

const MyIndustries: React.FC<IMyIndustryProps> = props => {
  const { isAdmin, isClient, isCreditSightsOpsSufOrRatings, dispatchToastError } = props;
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [sectorRollup, setSectorRollup] = useState([] as Bixby.Models.AccountUserIndustryRollup[]);
  const [isLoading, setIsLoading] = useState(true);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'expandAll', 'contractAll', 'separator', 'export'];
    return result;
  };

  const getRowId = useCallback(params => {
    return params.data.id;
  }, []);

  const handleFetchSectors = useCallback(async () => {
    try {
      setIsLoading(true);
      const sectorRollupResults = await AccountService.getSectors();
      setSectorRollup(sectorRollupResults);
    } catch (err) {
      dispatchToastError('Unable to load industries');
    } finally {
      setIsLoading(false);
    }
  }, [dispatchToastError]);

  useEffect(() => {
    if (isClient === true && handleFetchSectors) {
      handleFetchSectors();
    }
  }, [isClient, handleFetchSectors]);

  const handleStateChange = (record, newCheckedState, property) => {
    const id: string = record['id'];
    const sector_Id: string = record['sector_Id'];
    const recordType: string = record['type'];

    const relatedIndustryGroups: Bixby.Models.AccountUserIndustryRollup[] = [];
    const newValues = sectorRollup.map(existingValue => {
      // If this is sector, update it, and all related industry groups
      if (recordType === 'Sector') {
        if (existingValue.sector_Id === id) {
          existingValue[property] = newCheckedState;
        }
      }

      // If this is industry group, update it, and catalog industry groups to update sector later if necessary
      if (recordType === 'IndustryGroup') {
        if (existingValue.industryGroup_Id === id) {
          existingValue[property] = newCheckedState;
        }

        if (existingValue.type === 'IndustryGroup' && existingValue.sector_Id === sector_Id) {
          relatedIndustryGroups.push(existingValue);
        }
      }

      return existingValue;
    });

    // Update sector if necessary
    if (relatedIndustryGroups.length > 0 && relatedIndustryGroups.every(ig => ig[property] === newCheckedState)) {
      // Get sector
      const parentSector = newValues.find(s => s.id === sector_Id);
      if (parentSector) {
        parentSector[property] = newCheckedState;
      }
    }

    setSectorRollup(newValues);
  };

  const updateAlertState = async (id, params, newCheckedState) => {
    const type: string = params.data['type'];
    handleStateChange(params.data, newCheckedState, 'alertsEnabled');
    if (type === 'Sector') {
      await AccountService.updateAccountUserSectorAlertState(id, newCheckedState);
    } else if (type === 'IndustryGroup') {
      await AccountService.updateAccountUserIndustryGroupAlertState(id, newCheckedState);
    }
  };

  const updateMyPortfolioState = async (id, params, newCheckedState) => {
    const type: string = params.data['type'];
    handleStateChange(params.data, newCheckedState, 'myPortfolioEnabled');
    if (type === 'Sector') {
      await AccountService.updateAccountUserSectorMyPortfolioState(id, newCheckedState);
    } else if (type === 'IndustryGroup') {
      await AccountService.updateAccountUserIndustryGroupMyPortfolioState(id, newCheckedState);
    }
  };

  const content = isAdmin ? (
    <React.Fragment>
      <section>
        <div className="user-industries-section">
          <h3>Admin User</h3>
        </div>
      </section>
    </React.Fragment>
  ) : isLoading || Bixby.Utils.isNullOrEmpty(sectorRollup) ? (
    <Loading pageLevel />
  ) : isClient ? (
    <React.Fragment>
      <section>
        <div className="user-industries-note">
          <p>Individual company choices override sector and industry group choices.</p>
        </div>
        <div className="user-industries-section">
          <div className="user_industries_data_grid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  enableRangeSelection={false}
                  getContextMenuItems={getContextMenuItems}
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    sortable: true,
                    resizable: true,
                    lockPinned: true,
                  }}
                  onGridReady={onGridReady}
                  rowData={sectorRollup}
                  suppressDragLeaveHidesColumns={true}
                  suppressMovableColumns={true}
                  suppressCellFocus={true}
                  treeData={true}
                  animateRows={true}
                  groupDefaultExpanded={-1}
                  groupDisplayType="custom"
                  getDataPath={function (data) {
                    return data.name;
                  }}
                  getRowId={getRowId}
                  rowHeight={32}
                  headerHeight={32}
                  components={{
                    headerFilter: AnalyticsHeaderFilter,
                    bixToggleCellRenderer: BixToggleCellRenderer,
                  }}
                  columnDefs={[
                    {
                      headerGroupComponent: 'headerFilter',
                      cellClass: 'grid-cell-centered',
                      children: [
                        {
                          field: 'name',
                          cellRenderer: 'agGroupCellRenderer',
                          valueFormatter: params => params.node?.key || '',
                          cellClassRules: {
                            sector: params => {
                              return params.node.level === 0;
                            },
                            'industry-group': params => {
                              return params.node.level === 1;
                            },
                          },
                          headerClass: 'locked-header-note',
                          headerComponentParams: {
                            template:
                              '<div class="analytics-name">' +
                              '<span class="sector">Sector</span>' +
                              ' | <span class="industry-group">Industry Group</span>' +
                              '</div>',
                          },
                          minWidth: 350,
                          cellRendererParams: { suppressCount: true },
                          pinned: 'left',
                          showRowGroup: true,
                        },
                      ],
                    },
                    {
                      field: 'alertsEnabled',
                      headerName: 'Email Alerts',
                      minWidth: 100,
                      cellClass: 'align-center',
                      cellRenderer: 'bixToggleCellRenderer',
                      cellRendererParams: {
                        checkedValue: 'alertsEnabled',
                        idValue: 'id',
                        getComponentId: (params: ICellRendererParams) => {
                          return `${params.data['id']}.alertsEnabled`;
                        },
                        handleToggle: (id: string, newCheckedState: boolean, params: ICellRendererParams) => {
                          updateAlertState(id, params, newCheckedState);
                        },
                      },
                    },
                    {
                      field: 'myPortfolioEnabled',
                      headerName: 'MyPortfolio',
                      minWidth: 100,
                      hide: isCreditSightsOpsSufOrRatings,
                      cellClass: 'align-center',
                      cellRenderer: 'bixToggleCellRenderer',
                      cellRendererParams: {
                        checkedValue: 'myPortfolioEnabled',
                        idValue: 'id',
                        getComponentId: (params: ICellRendererParams) => {
                          return `${params.data['id']}.myPortfolioEnabled`;
                        },
                        handleToggle: (id: string, newCheckedState: boolean, params: ICellRendererParams) => {
                          updateMyPortfolioState(id, params, newCheckedState);
                        },
                      },
                    },
                  ]}></AgGridReact>
              </div>
            </div>
          </div>
        </div>
      </section>
    </React.Fragment>
  ) : null;
  return <SettingsNavigation>{content}</SettingsNavigation>;
};

const mapStateToProps = (state: AppState) => {
  const { isClient } = getIsCurrentUserClientState(state);
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { isCreditSightsAnalyst } = getIsCurrentUserCreditSightsAnalystState(state);
  const { isRatingsAnalyst } = getIsCurrentUserRatingsAnalystState(state);
  const { isSuFAnalyst } = getIsCurrentUserSuFAnalystState(state);
  const { isCreditSightsOps } = getIsCurrentUserCreditSightsOpsState(state);

  return {
    isAdmin,
    isClient,
    isCreditSightsOpsSufOrRatings: isCreditSightsAnalyst || isCreditSightsOps || isSuFAnalyst || isRatingsAnalyst,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyIndustries);
